import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export type HostingNowEvent = {
  id: string;
  title: string;
  venue: string | null;
  start_time: string;
  end_time: string;
  timezone: string | null;
  host_id: string;
  secondary_host_id?: string | null;
  is_canceled: boolean;
};

export const useHostingNow = () => {
  const { userId, isAuthenticated } = useAuth();

  const eventsQuery = useQuery({
    queryKey: ["hosting-now", userId],
    enabled: isAuthenticated && !!userId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<HostingNowEvent[]> => {
      const now = Date.now();
      const lowerEnd = new Date(now - 48 * 3600 * 1000).toISOString();
      const upperStart = new Date(now + 24 * 3600 * 1000).toISOString();

      // Hosts assigned only to a departure wave still run the event on the day.
      const { data: waves, error: wavesError } = await supabase
        .from("event_groups")
        .select("event_id")
        .or(`host_id.eq.${userId!},secondary_host_id.eq.${userId!}`);
      if (wavesError) throw wavesError;
      const waveEventIds = Array.from(new Set((waves ?? []).map((w) => w.event_id)));
      const hostFilter = [
        `host_id.eq.${userId!}`,
        `secondary_host_id.eq.${userId!}`,
        ...(waveEventIds.length > 0 ? [`id.in.(${waveEventIds.join(",")})`] : []),
      ].join(",");

      const { data, error } = await supabase
        .from("events")
        .select(
          "id, title, venue, start_time, end_time, timezone, host_id, secondary_host_id, is_canceled"
        )
        .or(hostFilter)
        .eq("is_canceled", false)
        .gte("end_time", lowerEnd)
        .lte("start_time", upperStart)
        .order("start_time", { ascending: true })
        .limit(3);

      if (error) throw error;
      return (data ?? []) as HostingNowEvent[];
    },
  });

  const hostingEvents = eventsQuery.data ?? [];
  const nowIso = new Date().toISOString();
  const pastEndedIds = hostingEvents
    .filter((e) => e.end_time < nowIso)
    .map((e) => e.id)
    .sort();

  const unmarkedQuery = useQuery({
    queryKey: ["hosting-now-unmarked", pastEndedIds],
    enabled: pastEndedIds.length > 0,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from("tickets")
        .select("event_id")
        .in("event_id", pastEndedIds)
        .eq("status", "confirmed")
        .is("attendance_status", null);

      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((row: { event_id: string }) => {
        counts[row.event_id] = (counts[row.event_id] ?? 0) + 1;
      });
      return counts;
    },
  });

  return {
    hostingEvents,
    unmarkedByEvent: unmarkedQuery.data ?? {},
    isLoading: eventsQuery.isLoading,
  };
};
